// pages/SubAdminLogin.jsx - Saffron Theme with Staff Authentication
import React, { useState, useEffect } from 'react';
import {
    Smartphone, Shield, Eye, EyeOff, Lock, Mail,
    ArrowRight, AlertCircle, CheckCircle2, Cloud,
    Check, Copy, Cpu, X, Key, Send, Layers, UserCog,
    LogIn
} from 'lucide-react';
import bgImage from '../assets/logoC.png';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { API_BASE } from '../config/api'; // Import API_BASE and getHeaders

// ── API Configuration ───────────────────────────────────────────────────────
// TODO: move BASE_URL to an env var (e.g. import.meta.env.VITE_API_BASE_URL)
const STAFF_API_BASE_URL = `${API_BASE}/api/superAdmin/staff`;

// ── Session Storage Helper Functions ────────────────────────────────────────
const storeStaffSession = (data) => {
    if (data.success && data.token && data.staff) {
        const staff = data.staff;

        // Clear any existing session data
        sessionStorage.clear();

        // Store authentication
        sessionStorage.setItem('subAdminAuth', 'true');
        sessionStorage.setItem('subAdminToken', data.token);

        // Store complete staff data
        sessionStorage.setItem('subAdminData', JSON.stringify({
            id: staff._id,
            name: staff.name,
            email: staff.email,
            role: staff.roleName || staff.role,
            roleId: staff.roleId,
            department: staff.departmentName || staff.department,
            departmentId: staff.departmentId,
            activeSessionId: staff.activeSessionId,
            phone: staff.phone,
            image: staff.image,
            address: staff.address,
            status: staff.status,
            createdAt: staff.createdAt,
            updatedAt: staff.updatedAt
        }));

        

        return true;
    }
    return false;
};

export const getStaffSession = () => {
    const auth = sessionStorage.getItem('subAdminAuth');
    const token = sessionStorage.getItem('subAdminToken');
    const staffData = sessionStorage.getItem('subAdminData');

    if (auth === 'true' && token && staffData) {
        try {
            return {
                isAuthenticated: true,
                token: token,
                staff: JSON.parse(staffData)
            };
        } catch (e) {
            return {
                isAuthenticated: false,
                token: null,
                staff: null
            };
        }
    }
    return {
        isAuthenticated: false,
        token: null,
        staff: null
    };
};

export const logoutStaff = () => {
    sessionStorage.removeItem('subAdminAuth');
    sessionStorage.removeItem('subAdminToken');
    sessionStorage.removeItem('subAdminData');
    sessionStorage.removeItem('staffId');
    sessionStorage.removeItem('staffName');
    sessionStorage.removeItem('staffEmail');
    sessionStorage.removeItem('staffRole');
    sessionStorage.removeItem('staffDepartment');
    sessionStorage.removeItem('staffPhone');
    sessionStorage.removeItem('staffImage');
};

// ── Auth Service (wired to live API) ────────────────────────────────────────
const AuthService = {
    login: async (email, password) => {
        const res = await fetch(`${STAFF_API_BASE_URL}/login`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ email, password })
        });
        const data = await res.json();
        if (!res.ok || data.success === false) {
            throw new Error(data.message || 'Invalid email or password');
        }
        return data;
    }
};

// ── Reusable Input Component ──────────────────────────────────────────────
const InputField = ({ icon: Icon, type, value, onChange, placeholder, toggleShow, showPassword }) => (
    <div className="relative">
        <Icon size={18} className="absolute left-4 top-1/2 -translate-y-1/2" style={{ color: '#5a4f72' }} />
        <input
            type={type}
            value={value}
            onChange={onChange}
            placeholder={placeholder}
            className="w-full h-12 rounded-xl pl-12 pr-12 outline-none transition-all"
            style={{
                background: 'rgba(255,125,56,0.08)',
                border: '1px solid rgba(255,125,56,0.2)',
                color: '#e2d9f3',
            }}
            onFocus={e => {
                e.currentTarget.style.borderColor = 'rgba(255,125,56,0.5)';
                e.currentTarget.style.boxShadow = '0 0 20px rgba(255,125,56,0.1)';
            }}
            onBlur={e => {
                e.currentTarget.style.borderColor = 'rgba(255,125,56,0.2)';
                e.currentTarget.style.boxShadow = 'none';
            }}
        />
        {toggleShow && (
            <button
                type="button"
                onClick={toggleShow}
                className="absolute right-4 top-1/2 -translate-y-1/2 transition-colors"
                style={{ color: '#5a4f72' }}
                onMouseEnter={e => e.currentTarget.style.color = '#9c8fc0'}
                onMouseLeave={e => e.currentTarget.style.color = '#5a4f72'}
            >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
        )}
    </div>
);

// ── Reusable Button Component ─────────────────────────────────────────────
const Button = ({ children, loading, onClick, type = 'submit', fullWidth = true, variant = 'primary' }) => {
    const isPrimary = variant === 'primary';
    const baseStyle = fullWidth ? 'w-full' : '';
    const colorStyle = isPrimary
        ? { background: 'linear-gradient(135deg, #FF7D38, #FF6B1A)', boxShadow: '0 0 20px rgba(255,125,56,0.3)' }
        : { background: 'rgba(255,125,56,0.1)', color: '#FF7D38', border: '1px solid rgba(255,125,56,0.15)' };

    return (
        <button
            type={type}
            onClick={onClick}
            disabled={loading}
            className={`${baseStyle} h-12 rounded-xl text-white font-semibold transition-all duration-300 disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2 ${isPrimary ? 'relative overflow-hidden group' : ''}`}
            style={colorStyle}
            onMouseEnter={e => {
                if (!e.currentTarget.disabled && isPrimary) {
                    e.currentTarget.style.boxShadow = '0 0 40px rgba(255,125,56,0.5)';
                    e.currentTarget.style.transform = 'scale(1.02)';
                } else if (!isPrimary) {
                    e.currentTarget.style.background = 'rgba(255,125,56,0.2)';
                }
            }}
            onMouseLeave={e => {
                if (isPrimary) {
                    e.currentTarget.style.boxShadow = '0 0 20px rgba(255,125,56,0.3)';
                    e.currentTarget.style.transform = 'scale(1)';
                } else {
                    e.currentTarget.style.background = 'rgba(255,125,56,0.1)';
                }
            }}
        >
            {isPrimary && (
                <span className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                    style={{ background: 'linear-gradient(135deg, rgba(255,125,56,0.3), rgba(255,107,26,0.3))' }}
                />
            )}
            {loading ? (
                <>
                    <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    <span>Loading...</span>
                </>
            ) : children}
        </button>
    );
};

export const SubAdminLogin = ({ onLogin }) => {
    const [state, setState] = useState({
        email: '',
        password: '',
        showPw: false,
        loading: false,
        success: false
    });

    const navigate = useNavigate();

    // Check if already logged in
    useEffect(() => {
        const session = getStaffSession();
        if (session.isAuthenticated) {
            navigate('/admin/staff-profile');
        }
    }, [navigate]);

    const updateState = (key, value) => setState(prev => ({ ...prev, [key]: value }));

    const handleSubmit = async (e) => {
        e.preventDefault();
        const { email, password } = state;

        if (!email || !password) {
            toast.error('Please fill in all fields.');
            return;
        }

        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            toast.error('Please enter a valid email address.');
            return;
        }

        updateState('loading', true);

        try {
            const data = await AuthService.login(email, password);

            // Store all session data using the helper function
            if (storeStaffSession(data)) {
                updateState('loading', false);
                updateState('success', true);
                toast.success('Login successful! Welcome back!');
                console.log("Session Data :::::", data)

                if (onLogin) onLogin(data.staff);

                // Navigate to dashboard
                navigate('/admin/staff-profile');
            } else {
                throw new Error('Failed to store session data');
            }
        } catch (err) {
            updateState('loading', false);
            toast.error(err.message || 'Invalid email or password');
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #02203C, #02203C)', boxShadow: '0 0 20px rgba(2,32,60,0.3)' }}>
            {/* Background */}
            <div className="absolute inset-0 z-0">
                <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, rgba(2,32,60,0.92), rgba(2,32,60,0.88), rgba(2,32,60,0.95))' }} />
                <div className="absolute inset-0" style={{ background: 'radial-gradient(circle at 30% 50%, rgba(255,125,56,0.08), transparent 70%)' }} />
            </div>

            {/* Main Container */}
            <div className="relative z-10 w-full max-w-6xl rounded-3xl shadow-2xl overflow-hidden" style={{ border: '1px solid rgba(255,125,56,0.2)', backdropFilter: 'blur(10px)' }}>
                <div className="flex flex-col lg:flex-row">
                    {/* Left Panel */}
                    <div className="relative lg:w-1/2 h-64 sm:h-72 md:h-80 lg:h-[600px] overflow-hidden">
                        <div className="absolute inset-0">
                            <img src={bgImage} alt="" className="w-full h-full object-cover" />
                        </div>
                        <div className="absolute inset-0 overflow-hidden">
                            {[...Array(30)].map((_, i) => (
                                <div key={i} className="absolute rounded-full animate-pulse" style={{
                                    width: Math.random() * 4 + 2 + 'px',
                                    height: Math.random() * 4 + 2 + 'px',
                                    background: 'rgba(255,255,255,0.15)',
                                    left: Math.random() * 100 + '%',
                                    top: Math.random() * 100 + '%',
                                    animationDuration: Math.random() * 4 + 2 + 's',
                                    animationDelay: Math.random() * 3 + 's',
                                }} />
                            ))}
                        </div>
                    </div>

                    {/* Right Panel - Login Form */}
                    <div className="lg:w-1/2 flex items-center justify-center p-6 sm:p-8 lg:p-12">
                        <div className="w-full max-w-sm">
                            <div className="mb-6 lg:mb-8">
                                <div className="flex items-center gap-3 mb-4">
                                    <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-orange-500/10">
                                        <UserCog size={22} style={{ color: '#e2d9f3' }} />
                                    </div>
                                    <h2 className="text-2xl sm:text-3xl font-bold" style={{ color: '#e2d9f3' }}>Staff Login</h2>
                                </div>
                                <p className="text-sm sm:text-base mt-1" style={{ color: '#5a4f72' }}>Enter your Staff credentials to access your account.</p>
                            </div>

                            <form onSubmit={handleSubmit} className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium mb-1.5" style={{ color: '#9c8fc0' }}>Staff Email Address</label>
                                    <InputField
                                        icon={Mail}
                                        type="email"
                                        value={state.email}
                                        onChange={e => updateState('email', e.target.value)}
                                        placeholder="Enter your staff email"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium mb-1.5" style={{ color: '#9c8fc0' }}>Password</label>
                                    <InputField
                                        icon={Lock}
                                        type={state.showPw ? "text" : "password"}
                                        value={state.password}
                                        onChange={e => updateState('password', e.target.value)}
                                        toggleShow={() => updateState('showPw', !state.showPw)}
                                        showPassword={state.showPw}
                                        placeholder="Enter your password"
                                    />
                                </div>

                                <Button loading={state.loading}>
                                    Sign In <LogIn size={18} className="group-hover:translate-x-1 transition-transform" />
                                </Button>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SubAdminLogin;